import React, { useState } from "react";
import LeadModal from "./LeadModal";
import { trackEvent } from "../utils/analytics";

const LeadsPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleOpenModal = (): void => {
    setIsModalOpen(true);
    trackEvent("cta_click", {
      source: "leads_page_mobile",
      action: "open_early_access_modal",
    });
  };

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-agro-light via-white to-agro-lighter">
      {/* Conteúdo Principal */}
      <main className="container mx-auto px-4 py-8 md:py-12">
        <div className="max-w-3xl mx-auto text-center">
          {/* Logo Centralizada */}
          <div className="mb-8 md:mb-12">
            <img
              src="/logo.png"
              alt="App Agro Negócios"
              className="h-24 md:h-32 lg:h-36 w-auto mx-auto drop-shadow-lg"
            />
          </div>

          {/* Título Principal */}
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-agro-dark mb-6 leading-tight px-2">
            O agro vai mudar. E você pode estar{" "}
            <span className="text-agro-primary">entre os primeiros</span> a
            aproveitar.
          </h1>

          {/* Subtítulo */}
          <div className="text-base md:text-lg text-gray-700 mb-10 max-w-2xl mx-auto px-4">
            <p>
              Cadastre-se agora para{" "}
              <span className="font-semibold text-agro-primary">
                receber novidades em primeira mão
              </span>{" "}
              e garantir{" "}
              <span className="font-semibold text-agro-primary">
                acesso antecipado
              </span>{" "}
              ao App AgroNegócios.
            </p>
          </div>

          {/* CTA Principal */}
          <div className="mb-12">
            <button
              onClick={handleOpenModal}
              className="w-full max-w-md btn-primary-gradient btn-pulse font-bold py-4 px-6 md:py-5 md:px-8 rounded-2xl text-base md:text-lg shadow-xl hover:shadow-2xl mx-auto block"
            >
              Quero estar entre os primeiros
            </button>
          </div>

          {/* Benefícios */}
          <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-10 mb-10 mx-2">
            <h2 className="text-lg md:text-xl font-bold text-agro-dark mb-6">
              Por que entrar agora?
            </h2>
            <div className="space-y-4 text-left">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-700 text-sm md:text-base">
                  Receba acesso prioritário assim que o app estiver disponível
                </p>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-700 text-sm md:text-base">
                  Conecte-se com oportunidades de venda, compra e relacionamento
                  no campo
                </p>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-700 text-sm md:text-base">
                  Fique por dentro das atualizações do pré-lançamento
                </p>
              </div>

              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
                <p className="text-gray-700 text-sm md:text-base">
                  Participe do movimento que está digitalizando o agro
                  brasileiro
                </p>
              </div>
            </div>
          </div>

          {/* CTA Secundário */}
          <div className="text-center px-4">
            <p className="text-gray-600 mb-6 text-xs md:text-sm">
              *ao clicar no botão você será direcionado para o formulário de
              cadastro
            </p>
            <button
              onClick={handleOpenModal}
              className="w-full max-w-md btn-primary-gradient btn-pulse font-bold py-4 px-6 md:py-5 md:px-8 rounded-2xl text-base md:text-lg shadow-xl hover:shadow-2xl mx-auto block"
            >
              Quero estar entre os primeiros
            </button>
          </div>

          {/* Espaçamento final para melhor UX mobile */}
          <div className="h-8"></div>
        </div>
      </main>

      <footer className="bg-agro-dark text-white py-8 mt-auto">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <div className="flex flex-col md:flex-row justify-center items-center gap-6 md:gap-12">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-agro-primary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <a
                  href="mailto:contato@agronegocios.com.br"
                  className="text-gray-300 hover:text-agro-primary transition-colors"
                >
                  contato@agronegocios.com.br
                </a>
              </div>
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-agro-primary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <span className="text-gray-300">CNPJ: 57.003.943/0001-95</span>
              </div>
            </div>
            <div className="border-t border-gray-700 pt-4 mt-4">
              <p className="text-gray-400 text-sm">
                © 2024 App AgroNegócios. Todos os direitos reservados.
              </p>
            </div>
          </div>
        </div>
      </footer>

      <LeadModal isOpen={isModalOpen} onClose={handleCloseModal} />
    </div>
  );
};

export default LeadsPage;
